import React from 'react';
import { motion } from 'framer-motion';
import { PageTransition } from '../components/PageTransition';
import { NetworkGraph } from '../components/NetworkGraph';

export const GraphOverview: React.FC = () => {
  return (
    <PageTransition className="graph-overview-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 mb-2"
          >
            <div className="p-2 bg-[rgba(236,72,153,0.1)] rounded-xl text-pink-500 border border-[rgba(236,72,153,0.2)]">
              <span className="icon-fallback">🌐</span>
            </div>
            <h1 className="text-3xl font-bold m-0 text-text-primary">Network Topology</h1>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-text-secondary max-w-2xl"
          >
            Interactive visualization of the Cora citation network structure.
          </motion.p>
        </div>
      </div>

      {/* Info cards row — sits ABOVE the graph, not overlaid on top */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4"
      >
        {[
          { label: 'Total Nodes', value: '2,708', color: '#22d3ee' },
          { label: 'Total Edges', value: '5,429', color: '#10b981' },
          { label: 'Avg Degree', value: '3.9',   color: '#8b5cf6' },
          { label: 'Classes',    value: '7',      color: '#f59e0b' },
        ].map(({ label, value, color }) => (
          <div
            key={label}
            style={{
              background: 'rgba(32,31,31,0.7)',
              border: '1px solid rgba(131,148,147,0.15)',
              borderRadius: 16,
              padding: '0.875rem 1.25rem',
            }}
          >
            <div style={{ fontSize: '0.65rem', color: '#839493', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 4 }}>
              {label}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '1.4rem', fontWeight: 700, color }}>
              {value}
            </div>
          </div>
        ))}
      </motion.div>

      {/* Graph container — fixed height so canvas always has room */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        style={{
          position: 'relative',
          height: 520,
          borderRadius: 24,
          overflow: 'hidden',
          border: '1px solid rgba(131,148,147,0.15)',
          background: 'rgba(32,31,31,0.7)',
        }}
      >
        <NetworkGraph />

        {/* Description overlay — bottom-left, small and unobtrusive */}
        <div style={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          zIndex: 10,
          background: 'rgba(15,23,42,0.85)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(131,148,147,0.15)',
          borderRadius: 12,
          padding: '0.6rem 1rem',
          maxWidth: 280,
        }}>
          <p style={{ fontSize: '0.72rem', color: '#b9cac9', margin: 0, lineHeight: 1.5 }}>
            Force-directed graph of the Cora citation network.
            <strong style={{ color: '#fff' }}> Nodes</strong> = papers,
            <strong style={{ color: '#fff' }}> edges</strong> = citations.
          </p>
        </div>

        {/* Physics hint — bottom center */}
        <div style={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 999,
          padding: '0.35rem 1rem',
          fontSize: '0.7rem',
          color: '#839493',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22d3ee', display: 'inline-block', animation: 'pulse 2s infinite' }} />
          Nodes interact via physics engine
        </div>
      </motion.div>
    </PageTransition>
  );
};
